import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  atlasAskStream,
  atlasAutonomy,
  atlasBrief,
  atlasContext,
  canSee,
  consoleAnswerLinks,
  labelForView,
  pathForView,
  type AtlasAnswerTurn,
  type AtlasAutonomyResponse,
  type AtlasBrief,
  type AtlasContext,
  type Role,
  type ViewId,
  ROLE_LABEL,
} from '@aithora/shared';
import { useConsoleRole, useSession } from '../session/SessionContext';
import { useQuery } from '../useQuery';
import { sx } from '../ui/designStyles';
import { atlasRoleOf, noDash } from '../pages/atlas/common';
import { useAiControls } from './aiControls';
import { ApprovalQueue } from './ApprovalQueue';
import { AtlasActivity } from './AtlasActivity';
import { AtlasToday, hasToday } from './AtlasToday';

/**
 * The right-hand Atlas panel (design: atPanelOpen, T17900; atlasVals L33128-33230), copied
 * from the design's markup. Everything in it is served:
 *   - the header count and the Scope line: GET /api/aithora/atlas/context (the systems in reach)
 *   - the opening briefing and the suggested questions: GET /api/aithora/atlas/brief
 *   - an ask: POST /api/atlas/ask (the governed, streamed agent)
 *   - Console V4: "Needs your OK" at the top of Ask & approve (shell/ApprovalQueue) and the
 *     Activity tab (shell/AtlasActivity), for the roles with the approval queue.
 * The autonomy dial and eval stay on the full Atlas page, so autonomy cannot be armed from
 * a panel that might be dismissed.
 *
 * Console V5: a Today tab, the default for staff roles (shell/AtlasToday, the design's sample
 * under its NOT BUILT YET label); Sources under each answer (the served sources event);
 * two page-specific questions per page that ask at once.
 */

const LINK = 'color:#2F5C97;font-weight:600;text-decoration:none;border-bottom:1px solid rgba(47,92,151,.34);';
const CAP = 'font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;';
/** The panel's tab (design actTabChatStyle / actTabActStyle, L35915). */
const tabStyle = (on: boolean) => 'display:inline-flex;align-items:center;height:38px;font-size:13px;font-weight:' + (on ? '700;color:#22272E;border-bottom:2px solid #264C7E;' : '600;color:#54606C;border-bottom:2px solid transparent;') + 'text-decoration:none;white-space:nowrap;';
const ROW_BOT = 'display:flex;gap:12px;align-items:flex-start;';
const ROW_YOU = ROW_BOT + 'justify-content:flex-end;';
const BUBBLE_BOT = 'min-width:0;flex:1;';
const BUBBLE_YOU = 'min-width:0;max-width:300px;background:rgba(79,138,214,.1);border:1px solid rgba(47,92,151,.2);border-radius:12px;padding:9px 13px;';

type ChipTone = 'grey' | 'green' | 'red' | 'amber';
const CHIP_TONE: Record<ChipTone, [string, string, string]> = {
  grey: ['#54606C', 'rgba(34,39,46,.07)', 'rgba(34,39,46,.13)'],
  green: ['#1F6B52', 'rgba(47,138,111,.12)', 'rgba(47,138,111,.3)'],
  red: ['#B3261E', 'rgba(179,38,30,.11)', 'rgba(179,38,30,.3)'],
  amber: ['#5B6B7C', 'rgba(91,107,124,.12)', 'rgba(91,107,124,.32)'],
};
const chipStyle = (t: ChipTone) => {
  const [c, bg, bd] = CHIP_TONE[t];
  return 'display:inline-flex;align-items:center;gap:7px;font-size:14px;font-weight:600;letter-spacing:.03em;border-radius:7px;padding:4px 10px;color:' + c + ';background:' + bg + ';border:1px solid ' + bd + ';';
};

/** The design's "On <page>" label (atCtxLabel) and its suggestions (atCtxActions). They fill the box; nothing runs on its own. */
const CTX_LABEL: Partial<Record<ViewId, string>> = {
  home: 'Aithora Home', atlas: 'Atlas', sales: 'Sales', oversight: 'Oversight', settings: 'Settings', performance: 'Performance', pulse: 'System Pulse',
  overview: 'Overview', worklist: 'Worklist', residents: 'Residents', guardian: 'Guardian', family: 'Family Portal', companion: 'Atlas', policy: 'Policy', finance: 'Finance', help: 'Help', atlasIntel: 'Atlas Intelligence',
};
const CONTEXT: Partial<Record<ViewId, [string, string][]>> = {
  // Console V5 (atCtxActions, V5 L41499): two per page.
  overview: [['What needs me first today?', 'ranked by risk and deadline, with the reason'], ['What changed since yesterday?', 'facility scores, incidents and money moved']],
  worklist: [['What should I do first?', 'orders the open items by risk and due time'], ['Who is falling behind?', 'late items by person, without blame']],
  residents: [['Who is most likely to fall this week?', 'ranked, with the signals behind each'], ['Whose care plan review is overdue?', 'lists them with the due date']],
  guardian: [['Explain this emergency', 'the signal chain, who was paged and who acknowledged'], ['What is still unclaimed?', 'open alerts with time since raised']],
  family: [['Which families have not heard from us this week?', 'by resident, with the last update'], ['Draft this week’s update for Walter’s family', 'from the care notes; you send it']],
  finance: [['Where are we losing money this month?', 'denials and missed captures, largest first'], ['Forecast next month’s collections', 'with the range, marked MODELED']],
  policy: [['Which policies are due for review?', 'by owner and date'], ['What would this change affect?', 'the rules, people and residents it touches']],
  pulse: [['What is broken right now?', 'open incidents with who owns each'], ['Is this outage getting worse?', 'the trend over the last hour']],
  performance: [['Why did this score move?', 'the inputs that changed, largest first'], ['Which building should we help first?', 'lowest score with the most room to improve']],
  sales: [
    ['Draft the follow-up', 'from the last logged activity on this account'],
    ['Prep this meeting', 'personalizes the one-pager from the catalog record'],
    ['Who else should be in the room?', 'maps the decision chain for this track'],
  ],
  oversight: [
    ['Explain this alert', 'shows the signal chain and the ledger entry behind it'],
    ['Why did the gate hold that?', 'names the guard, threshold and the operator who armed it'],
  ],
  settings: [
    ['What does L3 actually stop?', 'lists the actions restricted at this response level'],
    ['Who can change this?', 'shows the roles that hold this permission'],
  ],
};
const DEFAULT_CONTEXT: [string, string][] = [
  ['What am I looking at?', 'plain-language tour of this page'],
  ['What needs my attention?', 'ranked by severity, not recency'],
];

/** The role the design names Atlas's scope after (SCOPE_FOR). */
function scopeName(role: Role | null): string {
  if (role === 'RESIDENT') return 'Resident';
  if (role === 'FAMILY_MEMBER') return 'Family';
  if (role === 'MEMBER') return 'Member';
  return atlasRoleOf(role);
}

const listWords = (a: string[]) => (a.length === 1 ? a[0] : a.slice(0, -1).join(', ') + ' or ' + a[a.length - 1]);

type Part = { text: string; view: ViewId | null };
type Link = { text: string; view: ViewId };

/** Split served prose into paragraphs, and every name the answer read (evidence, sources, a page's name) into a link. */
function paragraphs(text: string, links: Link[]): Part[][] {
  const known = [...links].filter((l) => l.text.trim()).sort((a, b) => b.text.length - a.text.length);
  const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = known.length ? new RegExp('(' + known.map((l) => '\\b' + esc(l.text) + '\\b').join('|') + ')', 'g') : null;
  return noDash(text)
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => {
      if (!re) return [{ text: p, view: null }];
      const out: Part[] = [];
      let at = 0;
      for (const m of p.matchAll(re)) {
        const i = m.index ?? 0;
        if (i > at) out.push({ text: p.slice(at, i), view: null });
        out.push({ text: m[0], view: known.find((l) => l.text === m[0])!.view });
        at = i + m[0].length;
      }
      if (at < p.length) out.push({ text: p.slice(at), view: null });
      return out;
    });
}

/** Every console page by its own name, so "See Guardian." opens Guardian. Atlas itself is not a destination from Atlas. */
const PAGE_VIEWS = ['residents', 'guardian', 'worklist', 'oversight', 'finance', 'capture', 'performance', 'atlasIntel', 'policy', 'pulse'] as ViewId[];
const PAGE_LINKS: Link[] = PAGE_VIEWS.map((v) => ({ text: labelForView(v), view: v }));

interface Msg {
  you: boolean;
  text: string;
  chip: { label: string; tone: ChipTone } | null;
  links: Link[];
  acts: string[];
  /** The console pages the answer read from (the served sources event). */
  sources: { label: string; view: ViewId }[];
  /** Each incident, resident or alert the answer read, linked to its own page (the served links event). */
  items?: { label: string; path: string }[];
  failed?: boolean;
}

/** The design's chip on an answer: allowed and sealed, refused at the gate, or nothing composed. */
function answerChip(a: AtlasAnswerTurn): { label: string; tone: ChipTone } | null {
  if (!a.done) return null;
  const result = (a.gate?.result ?? '').toUpperCase();
  if (result === 'REFUSE' || result === 'REFUSED' || result === 'BLOCK' || result === 'DENY') return { label: 'gate: REFUSED · out of scope', tone: 'red' };
  if (!a.route?.live && !a.tools.length) return { label: 'no source · nothing composed', tone: 'amber' };
  if (a.withheld) return { label: 'gate: HOLD · part withheld', tone: 'amber' };
  return { label: 'gate: ALLOW · sealed to Truth Ledger', tone: 'green' };
}

export function AtlasPanel({ view, onClose }: { view: ViewId | null; onClose: () => void }) {
  const { client, session } = useSession();
  const navigate = useNavigate();
  const { role, realRole } = useConsoleRole();
  const [input, setInput] = useState('');
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const staff = !!role && role !== 'RESIDENT' && role !== 'FAMILY_MEMBER' && role !== 'MEMBER';
  const ctx = useQuery<AtlasContext>((signal) => atlasContext(client, { signal }), [client]);
  const brief = useQuery<AtlasBrief>((signal) => atlasBrief(client, { signal }), [client]);
  const ai = useAiControls();
  // Ask & approve / Activity (design actShow): only the roles with the approval queue.
  const tabs = !!ai?.queueRole && !!ai.approvals.data?.can;
  const tdOk = hasToday(role);
  const [tab, setTab] = useState<'today' | 'chat' | 'act'>(() => (tdOk ? 'today' : 'chat'));
  const onAct = tabs && tab === 'act';
  const onToday = tdOk && tab === 'today';
  // A text's tap link opens the queue: Ask & approve, not Today.
  useEffect(() => {
    if (ai?.focusId) setTab('chat');
  }, [ai?.focusId]);
  const pendN = ai?.approvals.data?.items.length ?? 0;
  const auto = useQuery<AtlasAutonomyResponse>((signal) => atlasAutonomy(client, { signal }), [client], { enabled: staff });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const go = (v: ViewId) => (e: { preventDefault: () => void }) => {
    e.preventDefault();
    onClose();
    navigate(pathForView(v));
  };
  const stop = (e: { preventDefault: () => void }) => e.preventDefault();

  const ask = async (q: string) => {
    const question = q.trim();
    if (!question || busy) return;
    const history = msgs.filter((m) => !m.failed && m.text).map((m) => ({ role: m.you ? ('user' as const) : ('assistant' as const), content: m.text }));
    setMsgs((m) => [...m, { you: true, text: question, chip: null, links: [], acts: [], sources: [] }, { you: false, text: '', chip: null, links: [], acts: [], sources: [] }]);
    setInput('');
    setTab('chat');
    setBusy(true);
    const fill = (a: AtlasAnswerTurn) =>
      setMsgs((m) =>
        m.map((x, i) =>
          i === m.length - 1
            ? {
                you: false,
                text: a.text,
                chip: answerChip(a),
                links: [...(a.evidence?.items ?? []).flatMap((it) => (it.view ? [{ text: it.name, view: it.view as ViewId }] : [])), ...a.sources.map((s) => ({ text: s.label, view: s.view }))],
                acts: a.done ? a.proposals.map((p) => p.label) : [],
                sources: a.done ? a.sources : [],
                items: a.done ? consoleAnswerLinks(a, role) : [],
              }
            : x,
        ),
      );
    try {
      fill(await atlasAskStream(client, { question, history }, { onTurn: fill }));
    } catch (e) {
      setMsgs((m) => m.map((x, i) => (i === m.length - 1 ? { you: false, text: e instanceof Error ? e.message : 'Atlas could not answer.', chip: { label: 'no source · nothing composed', tone: 'amber' }, links: [], acts: [], sources: [], failed: true } : x)));
    } finally {
      setBusy(false);
    }
  };

  // atSysCount / atScopeNote: what this role may read, from the systems Atlas reports in reach
  const sys = ctx.data?.systems ?? [];
  const who = scopeName(role);
  // The design signs the role in by its own name (role) and reads by its scope (SCOPE_FOR): Sales reads as Operator
  const signedAs = role ? ROLE_LABEL[role] : who;
  const denied = sys.filter((y) => !y.inReach).map((y) => y.name);
  const sysCount = ctx.data ? sys.filter((y) => y.inReach).length + ' of ' + sys.length + ' systems readable as ' + signedAs : '';
  const scopeNote = !ctx.data
    ? ''
    : denied.length
      ? 'Signed in as ' + signedAs + ', so Atlas cannot read ' + listWords(denied) + '. Questions needing them are refused at the gate.' + (realRole === 'SUPER_ADMIN' ? ' Change this with the role switcher above, not here.' : '')
      : 'Signed in as ' + signedAs + ', so Atlas may read every connected system.';

  // canPropose / canDraft from the autonomy level (the design's AUTO rungs)
  const level = auto.data?.autonomy.effectiveLevel ?? auto.data?.autonomy.level ?? 'assist';
  const canPropose = level === 'assist' || level === 'full';
  const canDraft = canPropose || level === 'suggest';
  const actNote = canPropose
    ? 'Shows a dry run first. Nothing executes from chat.'
    : canDraft
      ? 'Draft only at SUGGEST. Atlas will show the dry run for you to copy, but will not queue it.'
      : 'Blocked at OBSERVE · Answer only. Raise autonomy to let Atlas draft or queue this.';

  const briefMsg: Msg | null = brief.data?.text
    ? {
        you: false,
        text: brief.data.text,
        chip: { label: brief.data.real ? 'briefing · live' : 'briefing · sample data', tone: 'grey' },
        links: [
          ...(brief.data.evidence?.items ?? []).flatMap((it) => (it.view ? [{ text: it.name, view: it.view as ViewId }] : [])),
          ...(brief.data.links ?? []).flatMap((l) => {
            const v = PAGE_VIEWS.find((x) => pathForView(x) === l.to);
            return v ? [{ text: l.text, view: v }] : [];
          }),
        ],
        acts: [],
        sources: [],
      }
    : null;
  const all = [...(briefMsg ? [briefMsg] : []), ...msgs];
  const ctxLabel = (view && CTX_LABEL[view]) || 'this page';
  const ctxActions = (view && CONTEXT[view]) || DEFAULT_CONTEXT;
  const prompts = brief.data?.suggestions ?? [];
  const typed = input.trim();

  const renderPart = (w: Part, k: number): ReactNode =>
    w.view ? (
      <a key={k} href={pathForView(w.view)} onClick={go(w.view)} style={sx(LINK)}>
        {w.text}
      </a>
    ) : (
      <span key={k}>{w.text}</span>
    );

  return (
    <div role="presentation" style={sx('position:fixed;inset:0;z-index:70;display:flex;justify-content:flex-end;')}>
      <a href="#" aria-label="Close Atlas" onClick={(e) => { stop(e); onClose(); }} style={sx('flex:1;background:rgba(34,39,46,.28);text-decoration:none;')}></a>
      <div role="dialog" aria-modal="true" aria-label="Atlas" style={sx('width:440px;max-width:92vw;flex:0 0 auto;background:#FBF8F1;border-left:1px solid rgba(34,39,46,.16);box-shadow:-16px 0 44px rgba(34,39,46,.16);display:flex;flex-direction:column;min-height:0;')}>
        <div style={sx('flex:0 1 auto;min-width:0;display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:6px;padding:14px 16px;border-bottom:1px solid rgba(34,39,46,.11);')}>
          <img src="/aithora-mark.svg" alt="Atlas" style={sx('width:26px;height:26px;flex:0 0 26px;object-fit:contain;')} />
          <div style={sx('min-width:0;')}>
            <div style={sx('font-size:15px;font-weight:700;')}>Atlas</div>
            <div style={sx('font-size:14px;color:#54606C;')}>{sysCount}</div>
          </div>
          <span style={sx('flex:1;')}></span>
          {/* The full page only for roles that can open it; the panel is every role's. */}
          {canSee(role, 'atlas') && (
            <a href="#" onClick={go('atlas')} style={sx('display:inline-flex;align-items:center;height:28px;padding:0 11px;border-top:1px solid rgba(34,39,46,.14);color:#3C454F;font-size:13px;text-decoration:none;flex:0 1 auto;min-width:0;white-space:nowrap;')}>
              Open Atlas {'→'}
            </a>
          )}
          <a href="#" aria-label="Close" onClick={(e) => { stop(e); onClose(); }} style={sx('display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:7px;border:1px solid rgba(34,39,46,.16);color:#54606C;text-decoration:none;font-size:15px;flex:0 0 auto;white-space:nowrap;')}>
            {'×'}
          </a>
        </div>
        <div style={sx('flex:0 1 auto;min-width:0;display:flex;align-items:flex-start;gap:9px;padding:10px 16px;border-bottom:1px solid rgba(34,39,46,.09);')}>
          <span style={sx(CAP + 'flex:0 1 auto;min-width:0;padding-top:2px;')}>Scope</span>
          <span style={sx('min-width:min(100%,130px);flex:1 1 130px;font-size:13px;color:#54606C;line-height:1.45;')}>{scopeNote}</span>
        </div>
        {(tabs || tdOk) && (
          <div role="tablist" aria-label="Atlas" style={sx('flex:0 0 auto;display:flex;align-items:flex-end;gap:18px;padding:0 16px;border-bottom:1px solid rgba(34,39,46,.11);')}>
            {tdOk && (
              <a href="#" role="tab" aria-selected={onToday} onClick={(e) => { stop(e); setTab('today'); }} style={sx(tabStyle(onToday))}>
                Today
              </a>
            )}
            <a href="#" role="tab" aria-selected={!onAct && !onToday} onClick={(e) => { stop(e); setTab('chat'); }} style={sx(tabStyle(!onAct && !onToday))}>
              Ask &amp; approve
              {pendN > 0 && <span style={sx('display:inline-flex;align-items:center;justify-content:center;min-width:18px;height:18px;padding:0 5px;margin-left:6px;border-radius:9px;background:#264C7E;color:#fff;font-size:14px;font-weight:700;')}>{pendN}</span>}
            </a>
            {tabs && (
              <a href="#" role="tab" aria-selected={onAct} onClick={(e) => { stop(e); setTab('act'); }} style={sx(tabStyle(onAct))}>
                Activity
              </a>
            )}
          </div>
        )}
        <div style={sx('flex:1;min-height:0;overflow:auto;padding:16px;display:flex;flex-direction:column;gap:14px;')}>
          {onAct ? (
            <AtlasActivity team={role === 'SUPER_ADMIN' || role === 'OPERATOR'} onQueue={() => setTab('chat')} onClose={onClose} />
          ) : onToday ? (
            <AtlasToday role={role} name={session?.user.name ?? ''} onTab={setTab} onClose={onClose} />
          ) : (
          <div style={sx('display:flex;flex-direction:column;gap:14px;min-width:0;')}>
          <ApprovalQueue />
          {all.map((m, i) => (
            <div key={i} role={m.failed ? 'alert' : undefined} style={sx(m.you ? ROW_YOU : ROW_BOT)}>
              {!m.you && <img src="/aithora-mark.svg" alt="Atlas" style={sx('width:22px;height:22px;flex:0 0 22px;object-fit:contain;')} />}
              <div translate={m.text ? 'no' : undefined} style={sx(m.you ? BUBBLE_YOU : BUBBLE_BOT)}>
                {(m.text ? paragraphs(m.text, [...m.links, ...PAGE_LINKS]) : [[{ text: busy && i === all.length - 1 ? 'Thinking…' : '', view: null }]]).map((p, k) => (
                  <div key={k} style={sx('font-size:13px;line-height:1.6;color:#22272E;margin-bottom:8px;')}>
                    {p.map(renderPart)}
                  </div>
                ))}
                {m.acts.length > 0 && (
                  <div style={sx('display:flex;flex-direction:column;gap:6px;margin-top:9px;')}>
                    {m.acts.map((label) => (
                      <a
                        key={label}
                        href="#"
                        onClick={canDraft ? go('atlas') : stop}
                        style={sx('display:flex;flex-direction:column;padding:9px 12px;border-radius:9px;text-decoration:none;' + (canDraft ? 'background:rgba(79,138,214,.08);border:1px solid rgba(47,92,151,.26);' : 'background:rgba(34,39,46,.05);border:1px dashed rgba(34,39,46,.2);'))}
                      >
                        <span style={sx('font-size:13px;font-weight:600;color:' + (canDraft ? '#2F5C97' : '#54606C') + ';')}>{label}</span>
                        <span style={sx('font-size:14px;color:#54606C;margin-top:3px;')}>{actNote}</span>
                      </a>
                    ))}
                  </div>
                )}
                {m.chip && <span style={sx(chipStyle(m.chip.tone))}>{m.chip.label}</span>}
                {(m.items?.length ?? 0) > 0 && (
                  <div data-testid="atlas-answer-links" style={sx('display:flex;flex-direction:column;gap:3px;margin-top:8px;padding-top:7px;border-top:1px solid rgba(34,39,46,.08);')}>
                    <span style={sx('font-size:14px;letter-spacing:.1em;text-transform:uppercase;color:#54606C;font-weight:700;')}>Open</span>
                    {m.items!.map((l) => (
                      <a
                        key={l.path}
                        href={l.path}
                        onClick={(e) => {
                          e.preventDefault();
                          onClose();
                          navigate(l.path);
                        }}
                        style={sx('font-size:14px;font-weight:600;text-decoration:none;color:#2F5C97;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;')}
                      >
                        {noDash(l.label)}
                      </a>
                    ))}
                  </div>
                )}
                {m.sources.length > 0 && (
                  <div style={sx('display:flex;flex-direction:column;gap:3px;margin-top:8px;padding-top:7px;border-top:1px solid rgba(34,39,46,.08);')}>
                    <span style={sx('font-size:14px;letter-spacing:.1em;text-transform:uppercase;color:#54606C;font-weight:700;')}>Sources</span>
                    {m.sources.map((c) => (
                      <a key={c.label + c.view} href={pathForView(c.view)} onClick={go(c.view)} style={sx('display:flex;gap:6px;align-items:baseline;font-size:14px;text-decoration:none;color:#2F5C97;min-width:0;')}>
                        <span style={sx('font-weight:600;')}>{c.label}</span>
                        <span style={sx('color:#54606C;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;')}>{labelForView(c.view)}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          <div style={sx('border-top:1px solid rgba(34,39,46,.09);padding-top:12px;')}>
            <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:8px;')}>
              <span style={sx(CAP)}>On {ctxLabel}</span>
            </div>
            <div style={sx('display:flex;flex-direction:column;gap:6px;margin-bottom:11px;')}>
              {ctxActions.map(([label, note]) => (
                <a
                  key={label}
                  href="#"
                  onClick={(e) => {
                    stop(e);
                    void ask(label);
                  }}
                  style={sx('display:block;padding:8px 12px;border-top:1px solid rgba(34,39,46,.14);text-decoration:none;')}
                >
                  <span style={sx('display:block;font-size:13px;color:#3C454F;font-weight:600;')}>{label}</span>
                  <span style={sx('display:block;font-size:14px;color:#54606C;margin-top:2px;line-height:1.4;')}>{note}</span>
                </a>
              ))}
            </div>
          </div>
          </div>
          )}
        </div>
        {!onAct && (
        <div style={sx('flex:0 1 auto;min-width:0;border-top:1px solid rgba(34,39,46,.1);padding:12px 16px 15px;')}>
          <div style={sx('display:flex;flex-direction:column;gap:6px;margin-bottom:11px;')}>
            {prompts.map((p) => (
              <a
                key={p}
                href="#"
                onClick={(e) => {
                  stop(e);
                  void ask(p);
                }}
                style={sx('display:block;padding:8px 12px;border-top:1px solid rgba(34,39,46,.14);color:#3C454F;font-size:13px;text-decoration:none;')}
              >
                {noDash(p)}
              </a>
            ))}
          </div>
          <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;')}>
            <input
              ref={inputRef}
              type="text"
              aria-label="Ask Atlas"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void ask(input);
                }
              }}
              placeholder={'Ask Atlas…'}
              style={sx('flex:1 1 130px;min-width:min(100%,130px);height:40px;padding:0 14px;border-top:1px solid rgba(34,39,46,.14);font-size:13px;color:#22272E;font-family:inherit;box-sizing:border-box;')}
            />
            <a
              href="#"
              role="button"
              aria-label="Send"
              aria-disabled={!typed || busy || undefined}
              onClick={(e) => {
                stop(e);
                void ask(input);
              }}
              style={sx(
                'display:inline-flex;align-items:center;justify-content:center;width:40px;height:40px;flex:0 0 40px;border-radius:50%;font-size:17px;font-weight:700;text-decoration:none;white-space:nowrap;' +
                  (typed ? 'background:#2F5C97;color:#fff;' : 'background:#FCFAF4;color:#54606C;border:1px solid rgba(34,39,46,.2);'),
              )}
            >
              {'↑'}
            </a>
          </div>
          <div style={sx('font-size:14px;color:#54606C;text-align:center;margin-top:9px;')}>Governed by the Aithora gate {'·'} nothing is executed from chat</div>
        </div>
        )}
      </div>
    </div>
  );
}
